import { useSyncExternalStore } from "react";

export type AutoLockMinutes = 5 | 10 | 15 | 30 | 60;
export type ClipboardSeconds = 15 | 30 | 60 | 90;

export const AUTO_LOCK_OPTIONS: ReadonlyArray<AutoLockMinutes> = [5, 10, 15, 30, 60];
export const CLIPBOARD_OPTIONS: ReadonlyArray<ClipboardSeconds> = [15, 30, 60, 90];

/** Grace period before a hidden tab locks, when lock-on-hide is on. */
export const HIDDEN_LOCK_GRACE_MS = 30_000;
/** Revealed secrets fall back to dots after this long. */
export const REVEAL_TIMEOUT_MS = 20_000;

interface Preference<T> {
  readonly get: () => T;
  readonly set: (value: T) => void;
  readonly use: () => T;
}

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

function oneOf<T extends number>(options: ReadonlyArray<T>): (raw: string) => T | undefined {
  return (raw: string): T | undefined => options.find((option: T): boolean => String(option) === raw);
}

function parseBoolean(raw: string): boolean | undefined {
  return raw === "true" ? true : raw === "false" ? false : undefined;
}

/** localStorage-backed preference. Storage failures (private mode) keep the value for this tab only. */
function preference<T extends string | number | boolean>(key: string, fallback: T, parse: (raw: string) => T | undefined): Preference<T> {
  let memory: T | undefined;

  function get(): T {
    try {
      const raw = window.localStorage.getItem(key);
      const parsed = raw === null ? undefined : parse(raw);
      return parsed ?? memory ?? fallback;
    } catch {
      return memory ?? fallback;
    }
  }

  function set(value: T): void {
    memory = value;
    try {
      window.localStorage.setItem(key, String(value));
    } catch {
      // Storage unavailable: the in-memory value still applies.
    }
    for (const listener of listeners) {
      listener();
    }
  }

  function use(): T {
    return useSyncExternalStore(subscribe, get, (): T => fallback);
  }

  return { get, set, use };
}

const autoLock = preference<AutoLockMinutes>("pf.autoLockMinutes", 15, oneOf(AUTO_LOCK_OPTIONS));
const clipboardSeconds = preference<ClipboardSeconds>("pf.clipboardSeconds", 30, oneOf(CLIPBOARD_OPTIONS));
const lockOnHide = preference<boolean>("pf.lockOnHide", false, parseBoolean);
const breachCheck = preference<boolean>("pf.breachCheck", true, parseBoolean);

export const getAutoLockMinutes = autoLock.get;
export const setAutoLockMinutes = autoLock.set;
export const useAutoLockMinutes = autoLock.use;

export const getClipboardSeconds = clipboardSeconds.get;
export const setClipboardSeconds = clipboardSeconds.set;
export const useClipboardSeconds = clipboardSeconds.use;

export const getLockOnHide = lockOnHide.get;
export const setLockOnHide = lockOnHide.set;
export const useLockOnHide = lockOnHide.use;

export const getBreachCheck = breachCheck.get;
export const setBreachCheck = breachCheck.set;
export const useBreachCheck = breachCheck.use;
